import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { notificationApi, Notification, NotificationType } from '../utils/api';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  refreshNotifications: () => Promise<void>;
  markAsRead: (notificationIds: string[]) => Promise<void>;
  markSingleAsRead: (notificationId: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  deleteAllNotifications: (readOnly?: boolean) => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const { isConnected, onNewNotification } = useWebSocket();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshNotifications = useCallback(async () => {
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response = await notificationApi.getNotifications(50, 0, false);
      setNotifications(response.notifications);
      setUnreadCount(response.unread_count);
    } catch (err: any) {
      console.error('❌ Error refreshing notifications:', err);
      setError(err.message || 'Failed to load notifications');
      // Set to 0 on error to avoid showing stale count
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated || !user) return;

    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response.unread_count);
    } catch (err: any) {
      console.error('❌ Error refreshing unread count:', err);
    }
  }, [isAuthenticated, user]);

  const markAsRead = useCallback(async (notificationIds: string[]) => {
    try {
      await notificationApi.markAsRead(notificationIds);
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          notificationIds.includes(n.id) ? { ...n, is_read: true } : n
        )
      );
      // Refresh unread count
      await refreshUnreadCount();
    } catch (err: any) {
      console.error('Error marking notifications as read:', err);
      throw err;
    }
  }, [refreshUnreadCount]);

  const markSingleAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationApi.markSingleAsRead(notificationId);
      // Update local state
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, is_read: true } : n
        )
      );
      // Refresh unread count
      await refreshUnreadCount();
    } catch (err: any) {
      console.error('Error marking notification as read:', err);
      throw err;
    }
  }, [refreshUnreadCount]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Refresh unread count
      await refreshUnreadCount();
    } catch (err: any) {
      console.error('Error deleting notification:', err);
      throw err;
    }
  }, [refreshUnreadCount]);

  const deleteAllNotifications = useCallback(async (readOnly: boolean = false) => {
    try {
      await notificationApi.deleteAllNotifications(readOnly);
      // Refresh notifications
      await refreshNotifications();
    } catch (err: any) {
      console.error('Error deleting notifications:', err);
      throw err;
    }
  }, [refreshNotifications]);

  // Load notifications when authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      refreshNotifications();
    } else {
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [isAuthenticated, user, refreshNotifications]);

  // Listen for WebSocket notifications
  useEffect(() => {
    if (!isAuthenticated || !isConnected) return;

    const handleNotification = (notificationData: any) => {
      const notification: Notification = {
        ...notificationData,
        type: notificationData.type as NotificationType,
        created_at: notificationData.created_at,
        is_read: notificationData.is_read || false
      };
      
      // Add to beginning of list
      setNotifications(prev => [notification, ...prev]);
      
      // Update unread count if not read
      if (!notification.is_read) {
        setUnreadCount(prev => prev + 1);
      }
    };

    // Register notification handler
    onNewNotification(handleNotification);
  }, [isAuthenticated, isConnected, onNewNotification]);

  // Auto-refresh unread count periodically
  useEffect(() => {
    if (!isAuthenticated || !user) return;

    const interval = setInterval(() => {
      refreshUnreadCount();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [isAuthenticated, user, refreshUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        isLoading,
        error,
        refreshNotifications,
        markAsRead,
        markSingleAsRead,
        deleteNotification,
        deleteAllNotifications
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
